import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, PlayCircle, FileText, Headphones, Infinity as InfinityIcon, ShieldCheck, Heart, Sparkles, Check, Lock } from "lucide-react";
import SectionReveal from "@/components/SectionReveal";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import useSeo from "@/hooks/useSeo";

const MF_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Course",
  "@id": "https://pipbennett.com/the-rewired-life#course",
  "name": "The Rewired Life",
  "description": "A four-module digital coaching program guiding nervous-system safety, self-connection, manifestation from self-trust, and embodied congruence. Currently in pre-launch — waitlist open. $197 AUD lifetime access.",
  "provider": { "@id": "https://pipbennett.com/#organization" },
  "url": "https://pipbennett.com/the-rewired-life",
  "inLanguage": "en-AU",
  "offers": {
    "@type": "Offer",
    "price": "197.00",
    "priceCurrency": "AUD",
    "availability": "https://schema.org/PreOrder",
    "url": "https://pipbennett.com/the-rewired-life"
  },
  "hasCourseInstance": {
    "@type": "CourseInstance",
    "courseMode": "Online"
  }
};

const PIP_IMAGE =
  "https://images.leadconnectorhq.com/image/f_webp/q_80/r_1200/u_https://assets.cdn.filesafe.space/ZZ0MV02eR9AkUfkNW9rm/media/68918892600bc50615680821.png";

const MODULES = [
  {
    n: "01",
    title: "Safety — The Foundation",
    blurb: "Coming home to you. Why 'trying harder' has never been the answer — and why safety is the soil everything else grows from.",
  },
  {
    n: "02",
    title: "Connection — Love as New Leadership",
    blurb: "From calm into connection. Updating the operating system that's been running in the background. Learning to lead yourself with Love instead of Fear.",
  },
  {
    n: "03",
    title: "Creation — Manifestation From Self-Connection",
    blurb: "Where truth becomes magnetic. Not forcing, not vision-boarding — becoming available to the life that's already waiting for you.",
  },
  {
    n: "04",
    title: "Congruence — Living the Rewired Life",
    blurb: "The art of congruence. Where calm stops being something you practise and starts becoming who you are — the rewired life, fully lived.",
  },
];

const INCLUDED = [
  { icon: PlayCircle, label: "Four deep-dive teaching modules plus welcome & closing" },
  { icon: Headphones, label: "Morning & evening guided meditations — one set per module" },
  { icon: FileText, label: "Beautifully designed printable workbooks for every module" },
  { icon: InfinityIcon, label: "Lifetime access. Yours to return to whenever life asks you to" },
  { icon: ShieldCheck, label: "14-day peace-of-mind guarantee" },
];

const FAQ = [
  {
    q: "Is this therapy?",
    a: "No — and it's important I'm clear about that. The Rewired Life is a coaching and growth space, designed to support your awareness, your growth, and how you lead yourself forward. If you already work with a therapist or health professional, I warmly encourage you to bring this work into that space. And if anything ever feels beyond what's comfortable to hold on your own, please reach out for the right support. That's not a step back — it's self-leadership.",
  },
  {
    q: "How long does it take?",
    a: "Most people move through the four modules in 4–6 weeks, but you have lifetime access — go at the pace that serves you. The meditations are designed to be used daily and can accompany you indefinitely. This isn't a race; it's a return.",
  },
  {
    q: "What makes this different from other programs I've tried?",
    a: "Most programs try to fix you. This one invites you to meet yourself. We work with your nervous system — not against it. You'll learn why 'trying harder' has been exhausting, and exactly how rewiring actually happens (in small, loving moments, not big breakthroughs).",
  },
  {
    q: "I've tried manifestation / mindset / positive thinking before. Why will this work?",
    a: "Because we don't start with the mindset. We start with safety. Most manifestation work skips the step your nervous system actually needs — feeling safe enough to want, to change, to expand. When your body feels safe, everything else becomes possible. When it doesn't, no amount of affirmation will stick.",
  },
  {
    q: "What if it doesn't land for me?",
    a: "If within 14 days you feel this program isn't right for you, email me personally and I'll refund you in full — no questions, no awkwardness. I only want this in the hands of people it genuinely serves.",
  },
];

function MfWaitlistForm({ compact = false }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/waitlist", {
        name,
        email,
        interest: "The Rewired Life",
        message: "Wants to know when The Rewired Life opens for enrolment.",
      });
      setDone(true);
      toast.success("You're on The Rewired Life waitlist.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  if (done)
    return (
      <div
        className={`bg-sage/10 border border-sage/30 rounded-2xl p-6 text-center ${compact ? "" : "md:p-8"}`}
        data-testid="trl-waitlist-success"
      >
        <div className="inline-flex w-10 h-10 items-center justify-center rounded-full bg-sage text-cream mb-3">
          <Check size={16} strokeWidth={1.5} />
        </div>
        <div className="font-serif-display text-xl text-ink">You're on the list.</div>
        <p className="mt-2 text-sm text-ink-soft">
          Pip will be in touch the moment doors open — at the founding price of $197 AUD lifetime access.
        </p>
      </div>
    );

  return (
    <form
      onSubmit={submit}
      className={`grid grid-cols-1 ${compact ? "md:grid-cols-3" : "md:grid-cols-5"} gap-3`}
      data-testid="trl-waitlist-form"
    >
      <input
        required
        placeholder="First name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className={`${compact ? "" : "md:col-span-2"} bg-white border border-hairline rounded-full px-5 py-3 text-ink placeholder-ink-soft/60 focus:outline-none focus:border-sage transition`}
        data-testid="trl-waitlist-name"
      />
      <input
        required
        type="email"
        placeholder="Email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className={`${compact ? "" : "md:col-span-2"} bg-white border border-hairline rounded-full px-5 py-3 text-ink placeholder-ink-soft/60 focus:outline-none focus:border-sage transition`}
        data-testid="trl-waitlist-email"
      />
      <button
        type="submit"
        disabled={loading}
        className="bg-sage hover:bg-sage-dark text-cream rounded-full px-6 py-3 font-medium tracking-wide transition-all disabled:opacity-50"
        data-testid="trl-waitlist-submit"
      >
        {loading ? "Saving…" : "Join Waitlist"}
      </button>
    </form>
  );
}

export default function TheRewiredLife() {
  useSeo({
    title: "The Rewired Life — Pip Bennett's Signature Coaching Program (Waitlist Open)",
    description: "Pip Bennett's signature four-module digital coaching program — Safety, Connection, Creation, Congruence. $197 AUD lifetime access. Currently in pre-launch — join the waitlist to be the first through the door.",
    canonical: "https://pipbennett.com/the-rewired-life",
    jsonLd: MF_JSONLD,
  });
  const { user } = useAuth();
  const alreadyOwns = user && user !== false && user.has_making_fabulous_access;

  return (
    <div data-testid="page-the-rewired-life">
      {/* TOP BANNER — clear, simple, persistent. Removable when sales copy lands. */}
      {!alreadyOwns && (
        <a
          href="#trl-final-waitlist"
          className="block bg-sage text-cream text-center py-3 px-6 text-sm md:text-[15px] font-medium tracking-wide hover:bg-sage-dark transition"
          data-testid="trl-top-banner"
        >
          <span className="inline-flex items-center gap-3">
            <Lock size={12} strokeWidth={2} />
            <span>The Rewired Life is in pre-launch — </span>
            <span className="underline-offset-4 underline decoration-cream/40 hover:decoration-cream">Join the Waitlist</span>
            <ArrowRight size={14} strokeWidth={1.8} />
          </span>
        </a>
      )}

      {/* HERO */}
      <section className="relative pt-12 md:pt-20 pb-24 md:pb-32 overflow-hidden">
        <div
          className="absolute inset-0 -z-10"
          style={{ background: "linear-gradient(135deg, #F5EDE4 0%, #E3EBE4 60%, #F1E0D0 100%)" }}
        />
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <SectionReveal className="lg:col-span-7">
            <div className="eyebrow mb-6 text-gold-dark inline-flex items-center gap-2">
              <Lock size={12} strokeWidth={2} /> Pre-launch · Waitlist Open · $197 AUD Lifetime Access
            </div>
            <h1 className="font-serif-display text-5xl md:text-6xl lg:text-7xl text-ink leading-[1.02]">
              The <em className="italic text-sage">Rewired</em> Life.
            </h1>
            <p className="mt-8 text-lg md:text-xl text-ink leading-relaxed max-w-xl">
              The signature digital program for anyone done second-guessing themselves. Four modules,
              morning &amp; evening meditations, workbooks you'll return to long after you're done. All designed
              to help your nervous system learn that calm is safe, that Love can lead, and that you are worthy of
              peace without having to earn it.
            </p>
            <div className="mt-8 bg-cream-soft border border-hairline rounded-2xl p-6 md:p-8 max-w-xl" data-testid="trl-prelaunch-banner">
              <div className="eyebrow mb-2 text-sage">A note from Pip</div>
              <p className="text-ink leading-relaxed">
                The Rewired Life is in its final pre-launch — I'm recording the teaching videos
                and meditations in their finished form. Add your name below and you'll be the
                first through the door at the founding price of $197 AUD, lifetime access.
              </p>
            </div>
            <div className="mt-8 max-w-xl">
              {alreadyOwns ? (
                <Link
                  to="/dashboard/the-rewired-life"
                  className="inline-flex items-center gap-2 bg-sage hover:bg-sage-dark text-cream rounded-full px-8 py-4 font-medium tracking-wide transition-all"
                  data-testid="trl-dashboard-cta"
                >
                  Go to your dashboard
                  <ArrowRight size={16} strokeWidth={1.8} />
                </Link>
              ) : (
                <MfWaitlistForm />
              )}
            </div>
          </SectionReveal>
          <SectionReveal delay={150} className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-8 bg-sage/15 rounded-[2rem] blur-2xl -z-10" />
              <img
                src={PIP_IMAGE}
                alt="Pip Bennett"
                className="w-full rounded-[1.5rem] object-cover shadow-[0_30px_80px_-30px_rgba(74,93,79,0.35)]"
              />
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* HERO PULL-QUOTE — her voice */}
      <section className="bg-peach/70 py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <SectionReveal>
            <div className="font-serif-display text-3xl md:text-5xl lg:text-6xl text-sage leading-[1.12] italic">
              "You were never too much. You were just around people who felt safer when you dimmed.
              And that story ends here."
            </div>
            <div className="mt-8 eyebrow text-ink">— Pip</div>
          </SectionReveal>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="py-20 md:py-28 bg-cream-soft border-b border-hairline">
        <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-14">
          <SectionReveal>
            <div className="eyebrow mb-4 text-sage">What You Get</div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-ink leading-tight max-w-3xl">
              Everything you need — and nothing you don't.
            </h2>
          </SectionReveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {INCLUDED.map((item, i) => (
              <SectionReveal key={i} delay={i * 80}>
                <div className="bg-white border border-hairline rounded-2xl p-8 h-full">
                  <item.icon size={24} strokeWidth={1.4} className="text-sage mb-4" />
                  <p className="text-sm text-ink leading-relaxed">{item.label}</p>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* THE ARC */}
      <section className="py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <SectionReveal>
            <div className="eyebrow mb-4 text-sage">The Journey</div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-ink leading-tight max-w-3xl">
              Safety. Connection. Creation. Congruence.
            </h2>
            <p className="mt-6 text-ink-soft max-w-2xl leading-relaxed">
              This work moves in a very particular order — because your nervous system needs it to. Safety first.
              Connection second. Creation third. Congruence, fourth. That's how we rewire from the inside out.
            </p>
          </SectionReveal>

          <div className="mt-14 space-y-4">
            {MODULES.map((m, i) => (
              <SectionReveal key={i} delay={i * 60}>
                <div className="group bg-white border border-hairline rounded-2xl p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-6 hover:border-sage hover:shadow-[0_20px_40px_-20px_rgba(74,93,79,0.12)] transition-all">
                  <div className="font-serif-display text-5xl md:text-6xl text-gold/80 font-light tabular-nums flex-shrink-0 w-20">
                    {m.n}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-serif-display text-2xl md:text-3xl text-ink leading-tight">{m.title}</h3>
                    <p className="mt-3 text-ink-soft leading-relaxed">{m.blurb}</p>
                  </div>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* PULL QUOTE 2 */}
      <section className="py-20 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <SectionReveal>
            <Heart size={28} strokeWidth={1.3} className="mx-auto text-gold mb-8" />
            <div className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-ink leading-[1.15]">
              "It's the difference between <em className="italic text-sage">surviving</em> yourself
              and <em className="italic text-sage">living from</em> yourself."
            </div>
            <div className="mt-8 eyebrow text-ink-soft">— From Module Four</div>
          </SectionReveal>
        </div>
      </section>

      {/* PRICE / WAITLIST */}
      <section className="py-24 md:py-32 bg-sage text-cream">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <SectionReveal>
            <div className="eyebrow mb-6 text-gold inline-flex items-center gap-2">
              <Lock size={12} strokeWidth={2} /> Pre-launch · Doors Opening Soon
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-tight">
              The founding price is $197 AUD. Lifetime access.
            </h2>
            <p className="mt-8 max-w-xl mx-auto text-cream/85 leading-relaxed text-lg">
              I'm in the studio recording the finished teaching videos and meditations. Add your name
              and you'll be the very first to hear when doors open — at the founding price of
              $197 AUD, with everything beautifully complete.
            </p>
            {alreadyOwns ? (
              <Link
                to="/dashboard/the-rewired-life"
                className="mt-10 inline-flex items-center gap-2 bg-gold hover:bg-gold-dark text-ink rounded-full px-10 py-5 font-medium tracking-wide transition-all"
                data-testid="trl-price-cta-owned"
              >
                Enter your dashboard
                <ArrowRight size={16} strokeWidth={1.8} />
              </Link>
            ) : (
              <div className="mt-10 max-w-md mx-auto" data-testid="trl-price-waitlist">
                <MfWaitlistForm compact />
                <p className="mt-4 text-xs text-cream/60">
                  No spam. Just one warm note when the program opens.
                </p>
              </div>
            )}
          </SectionReveal>
        </div>
      </section>

      {/* PIP'S PROMISE */}
      <section className="py-24 md:py-28">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
          <SectionReveal>
            <div className="eyebrow mb-6 text-sage">A Word From Pip</div>
            <div className="font-serif-display text-2xl md:text-3xl text-ink leading-[1.35] space-y-6">
              <p>
                You're not learning how to become someone new. You're learning how to live as the version of you
                that's <em className="italic text-sage">already been there</em>, waiting underneath it all.
              </p>
              <p className="text-ink-soft text-xl md:text-2xl">
                No pressure. No urgency. You're already becoming.
              </p>
            </div>
            <div className="mt-10 font-serif-display text-2xl italic text-sage">— Love, Pip xox</div>
          </SectionReveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 bg-cream-soft border-t border-hairline">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <SectionReveal>
            <div className="eyebrow mb-4 text-sage">Questions</div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-ink leading-tight">You might be wondering…</h2>
          </SectionReveal>
          <div className="mt-14 space-y-4">
            {FAQ.map((f, i) => (
              <SectionReveal key={i} delay={i * 60}>
                <details className="group bg-white border border-hairline rounded-2xl p-8 transition-all" data-testid={`trl-faq-${i}`}>
                  <summary className="cursor-pointer flex items-start justify-between gap-6 list-none">
                    <span className="font-serif-display text-lg md:text-xl text-ink">{f.q}</span>
                    <span className="mt-1 text-sage text-xl leading-none group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="mt-5 text-ink-soft leading-relaxed">{f.a}</p>
                </details>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section id="trl-final-waitlist" className="py-24 md:py-32 scroll-mt-16">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
          <SectionReveal>
            <Sparkles size={28} strokeWidth={1.3} className="mx-auto text-gold mb-8" />
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-ink leading-[1.08]">
              The door isn't quite open yet — but it's close.
            </h2>
            <p className="mt-6 text-ink-soft max-w-xl mx-auto leading-relaxed">
              Add your name to the waitlist and you'll know the moment Pip's finished recording the
              final pieces. In the meantime — <Link to="/the-menopause-invitation" className="text-sage underline-offset-4 hover:underline">The Menopause Invitation</Link> is fully open.
            </p>
            {!alreadyOwns && (
              <div className="mt-10 max-w-md mx-auto" data-testid="trl-final-waitlist">
                <MfWaitlistForm compact />
              </div>
            )}
          </SectionReveal>
        </div>
      </section>
    </div>
  );
}
